import React, { useState, useEffect, useRef } from 'react';
import _path from 'ramda/src/path';
import PropTypes from 'prop-types';
import LogglyLogger from '../../../API/Loggly';
import SaveReblast from '../../../API/SaveReblast';
import resx from '../../../utilities/resx';
import { LOGGLY_LEVEL_ERROR, RESX_GLOBAL, RESX_REBLASTS, MIXPANEL_CONTENT } from '../../../Constants';
import { withMessage } from '../Message';
import UserMention from '../UserMention';
import CommentAttachment from '../Attachment/CommentAttachment';
import CommentAttachmentPreview from '../Attachment/CommentAttachmentPreview';
import Mixpanel from '../../../API/Mixpanel';

function CreatePost(props) {
    const queryParams = new URLSearchParams(window.location.search);

    const [attachmentInfo, setAttachmentInfo] = useState({});
    const [stateReblastContainer, setStateReblastContainer] = useState('re-postreblasts-condensed');
    const [stateReblastPost, setStateReblastPost] = useState('re-btn-disabled');
    const [socialMediaString, setSocialMediaString] = useState();
    const [inputCounter, setInputCounter] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [inputPlaceholder, setInputPlaceholder] = useState('');
    const [textAreaHeight, setTextAreaHeight] = useState('');
    const [buttonStatus, setButtonStatus] = useState('');

    const refInputEl = useRef(null);

    const userDomain = _path(['RESAAS', 'User2', 'DomainUrl'], window);
    const createListingUrl = `${userDomain}/listings/create`;
    const createLeadUrl = '/r/create';
    const message = props.message;

    const setPlaceholder = () => {
        const PLACEHOLDER_COUNT = 4;
        const rng = Math.floor(Math.random() * PLACEHOLDER_COUNT);
        const placeholder = resx(RESX_REBLASTS, `REBLAST_SAMPLE_${rng}`);
        setInputPlaceholder(placeholder);
    };

    const updateInputCounter = input => {
        setInputCounter(1000 - input.length);
    };

    const initializeInput = () => {
        setStateReblastPost('re-btn-disabled');

        // set default hashtag value
        if (props.hashtag) {
            setInputValue(`#${props.hashtag}`);
            updateInputCounter(`#${props.hashtag}`);
            return;
        }

        if (queryParams.get('createpost')) {
            expandReblastContainer();
            refInputEl.current.focus();
        }
        setInputValue(``);
        setTextAreaHeight(``);
        updateInputCounter(``);
    };

    const initializeSocialMedia = () => {
        // Set social media string
        const hasLinkedIn =
            _path(['RESAAS', 'User2', 'HasLinkedIn'], window) === true &&
            _path(['RESAAS', 'User2', 'IsLinkedInTokenValid'], window) === true;
        const hasTwitter = _path(['RESAAS', 'User2', 'HasTwitter'], window) === true;
        if (hasTwitter || hasLinkedIn) {
            let sb = resx(RESX_GLOBAL, 'SHARE_REBLAST_NOTIFICATION');
            if (hasTwitter) {
                sb = `${sb} Twitter`;
            }
            if (hasTwitter && hasLinkedIn) {
                sb = `${sb} &`;
            }
            if (hasLinkedIn) {
                sb = `${sb} LinkedIn`;
            }
            setSocialMediaString(sb);
        }
    };

    const updateTextboxHeightBasedOnScrollHeight = () => {
        // only autogrow, never shrink
        if (refInputEl.current.scrollHeight > textAreaHeight) {
            setTextAreaHeight(refInputEl.current.scrollHeight);
        }
    };

    useEffect(() => {
        initializeInput();

        setPlaceholder();

        initializeSocialMedia();

        const updatePlaceholder = setTimeout(updateTextboxHeightBasedOnScrollHeight, 1000);

        return () => {
            clearTimeout(updatePlaceholder);
        };
    }, []);

    const expandReblastContainer = () => {
        setStateReblastContainer('');
    };

    const handleInput = event => {
        const { value } = event.target;
        setInputValue(value);
        if (value.length > 0 && props.isGroupMember) {
            setStateReblastPost('');
        } else {
            setStateReblastPost('re-btn-disabled');
        }

        updateTextboxHeightBasedOnScrollHeight();
        updateInputCounter(value);
    };

    const validateInput = () => {
        if (inputValue.length > 1000) {
            message.addMessage({ message: resx(RESX_REBLASTS, 'REBLAST_CHAR_ERROR'), type: 'error' });
            return false;
        }
        return true;
    };

    const clickedPost = () => {
        if (!validateInput()) {
            return;
        }

        let data = {
            Message: inputValue,
            Publish: true
        };

        if (props.firmorgroup) {
            if (props.firmorgroup.IsFirm) {
                data = {
                    Message: inputValue,
                    ReblastType: 1
                };
            } else if (props.firmorgroup.isReferralGroup) {
                data = {
                    Message: inputValue,
                    GroupId: props.firmorgroup.GroupId,
                    ReblastType: 3
                };
            } else {
                data = {
                    Message: inputValue,
                    GroupId: props.firmorgroup.GroupId,
                    ReblastType: 2
                };
            }
        }

        if (attachmentInfo.commentsAttachmentsId) {
            data.ContentId = attachmentInfo.commentsAttachmentsId;
        }
        if (attachmentInfo.commentsAttachmentTypeId) {
            data.ContentType = attachmentInfo.commentsAttachmentTypeId;
        }

        setButtonStatus('submitting');
        SaveReblast.add(data)
            .then(response => {
                if (!_path(['data', 'Identifier'], response)) {
                    throw new Error('the new reblast creation was failed');
                }

                if (
                    window.location.href.indexOf('/feed/local') !== -1 ||
                    window.location.href.indexOf('/hashtag') !== -1
                ) {
                    message.addMessage({ message: resx(RESX_REBLASTS, 'POST_TO_LOCALFEED_SUCCESS'), type: 'success' });
                } else {
                    message.addMessage({ message: resx(RESX_REBLASTS, 'POST_TO_RESAAS_SUCCESS'), type: 'success' });
                }

                const request = JSON.parse(response.config.data);
                let mixpanelContent = 'Reblast Text';
                if (request.ContentType === '4') {
                    mixpanelContent = 'Reblast Photo';
                }
                if (request.ContentType === '5') {
                    mixpanelContent = 'Reblast Document';
                }

                const mpRequest = { content: mixpanelContent, from: window.pageType };
                const linkType = window.reGeneralUtilities.attachedLinkType(request.Message);
                if (linkType) {
                    mpRequest['link type'] = linkType;
                }
                Mixpanel.add(MIXPANEL_CONTENT, mpRequest);
                let newReblasts = JSON.parse(sessionStorage.getItem('newReblast'));
                response.data.FeedPage = window.location.pathname;
                if (newReblasts) {
                    newReblasts.unshift(response.data);
                } else {
                    newReblasts = [response.data];
                }
                sessionStorage.setItem('newReblast', JSON.stringify(newReblasts));

                setInputPlaceholder(resx(RESX_REBLASTS, 'POST_ANOTHER_REBLAST'));
                setAttachmentInfo({});
                initializeInput();
            })
            .catch(error => {
                message.addMessage({ message: resx(RESX_REBLASTS, 'UNABLE_REBLAST'), type: 'error' });
                LogglyLogger.add(LOGGLY_LEVEL_ERROR, 'Failed to post reblast from react', error);
            })
            .finally(() => {
                setButtonStatus('');
            });
    };

    let postText = resx(RESX_REBLASTS, 'POST_UPPERCASE');
    let lockClassname = '';
    if (props.firmorgroup) {
        if (props.firmorgroup.IsFirm) {
            postText = resx(RESX_GLOBAL, 'POST_PRIVATELY');
            lockClassname = 're-icon re-icon-sm re-icon-lock';
        } else {
            postText = resx(RESX_GLOBAL, 'POST_TO_GROUP_TEXT');
        }
    }

    return (
        <React.Fragment>
            {/* content creation buttons */}
            <div className="re-contentcreation-buttons js-contentcreation-buttons">
                <div className={`re-btn re-btn-small re-btn-white re-icon re-icon-plus re-icon-xs`}>
                    {resx(RESX_REBLASTS, 'POST_UPPERCASE')}
                </div>
                <a
                    href={createListingUrl}
                    className="js-createlisting-btn re-btn re-btn-small re-content-post re-icon re-icon-plus re-icon-xs"
                >
                    {resx(RESX_REBLASTS, 'LISTING_UPPERCASE')}
                </a>
                <a
                    href={createLeadUrl}
                    className="js-createlead-btn re-btn re-btn-small re-content-post re-icon re-icon-plus re-icon-xs"
                >
                    {resx(RESX_REBLASTS, 'LEAD_UPPERCASE')}
                </a>
            </div>

            {/* adding reblasts */}
            <div
                className={`re-component-profile-add-reblasts js-feedssearch-postreblasts formV2 re-container-outer ${stateReblastContainer}`}
                onMouseDown={expandReblastContainer}
            >
                {/* main add reblasts */}
                <div className="add-reblasts-main">
                    <div className="add-reblast-textarea re-textfield-with-tip">
                        <textarea
                            name="ctl00$Body$txtMessage"
                            id="ctl00_Body_txtMessage"
                            value={inputValue}
                            ref={refInputEl}
                            onChange={handleInput}
                            onClick={handleInput}
                            onBlur={handleInput}
                            placeholder={inputPlaceholder}
                            style={textAreaHeight ? { height: `${textAreaHeight}px` } : {}}
                            className="re-add-reblasts-input autogrow re-textarea-short"
                            autoComplete="off"
                            title=""
                        />
                        <UserMention inputValue={inputValue} setInputValue={setInputValue} inputRef={refInputEl} />
                        <span className="counter" style={{ display: 'inline' }}>
                            {inputCounter}
                        </span>
                        <span className="re-textfield-tip">
                            {props.isGroupMember
                                ? resx(RESX_GLOBAL, 'TIP_MENTION_A_CONNECTION')
                                : resx(RESX_GLOBAL, 'TIP_CREATE_POST_IN_GROUP')}
                        </span>
                    </div>
                    {attachmentInfo.commentsAttachmentsImage && (
                        <CommentAttachmentPreview data={attachmentInfo} update={setAttachmentInfo} />
                    )}
                </div>

                {/* bottom add reblasts */}
                <div className="add-reblasts-bottom">
                    {socialMediaString && (
                        <div className="re-social-network-verbiage">
                            <ul className="social-network-firms list-inline">
                                <li className="share-notification">{socialMediaString}</li>
                            </ul>
                        </div>
                    )}

                    <ul className="add-reblasts-actions list-inline">
                        <CommentAttachment data={attachmentInfo} update={setAttachmentInfo} />

                        <li className="actions-submit-reblasts pull-right">
                            <button
                                onClick={clickedPost}
                                type="submit"
                                name="ctl00$Body$btnAddReblast"
                                value="{postText}"
                                id="ctl00_Body_btnAddReblast"
                                className={`re-btn-blastoff re-btn re-btn-small ${stateReblastPost} ${buttonStatus} ${lockClassname}`}
                            >
                                {postText}
                            </button>
                        </li>
                    </ul>

                    <label className="add-reblasts-message" />
                </div>
            </div>
        </React.Fragment>
    );
}

CreatePost.propTypes = {
    message: PropTypes.object,
    hashtag: PropTypes.string,
    firmorgroup: PropTypes.object,
    isGroupMember: PropTypes.bool
};

CreatePost.defaultProps = {
    isGroupMember: true
};

export default withMessage(CreatePost);
